import React, { useState } from "react";
import {
  Toolbar,
  styled,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  FormControl,
  alpha,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../assets/images/logo.png";
import SearchBar from "../../../components/top-bar/SearchBar";
import Cart from "../../../components/top-bar/Cart";
import UserMenu from "../../../components/top-bar/UserMenu";
import LanguageSelector from "../../../lang/LanguageSelector";
import { useTranslation } from "react-i18next";

// Styled components
const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(0, 3),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  height: 72,
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(2),
  transition: "all 0.3s ease",

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 1.5),
    height: 64,
    gap: theme.spacing(1),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0, 0.75),
    height: 56,
    gap: theme.spacing(0.5),
  },
}));

const LogoContainer = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  textDecoration: "none",
  color: "inherit",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  flex: "0 0 auto",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "transparent",
    transition: "background 0.3s ease",
    zIndex: 0,
    borderRadius: "inherit",
  },

  "&:hover": {
    transform: "translateY(-1px)",
    "&::before": {
      background:
        theme.palette.mode === "light"
          ? "rgba(0, 0, 0, 0.02)"
          : "rgba(255, 255, 255, 0.02)",
    },
  },

  "&:active": {
    transform: "translateY(0)",
  },

  [theme.breakpoints.up("md")]: {
    minWidth: 200,
    padding: theme.spacing(1, 2),
  },

  [theme.breakpoints.down("sm")]: {
    flex: "0 1 auto",
    minWidth: "auto",
    padding: theme.spacing(0.5, 1),
    gap: theme.spacing(0.75),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.25, 0.5),
    gap: theme.spacing(0.5),
  },
}));

const LogoImage = styled("img")(({ theme }) => ({
  height: 58,
  width: "auto",
  objectFit: "contain",
  flexShrink: 0,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  filter: "brightness(1.02)",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
  backgroundColor: "transparent",

  "&:hover": {
    transform: "translateY(-1px) scale(1.02)",
    filter: "brightness(1.05) contrast(1.05)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
  },

  [theme.breakpoints.up("md")]: {
    height: 64,
  },

  [theme.breakpoints.down("sm")]: {
    height: 42,
    width: "auto",
    padding: theme.spacing(0.25),
  },
  
  [theme.breakpoints.down(400)]: {
    height: 36,
  },

  "@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)": {
    imageRendering: "crisp-edges",
  },
}));

const BrandName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.5rem",
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginLeft: theme.spacing(1),
  whiteSpace: "nowrap",
  lineHeight: 1.2,
  transition: "font-size 0.3s ease",

  [theme.breakpoints.down("sm")]: {
    fontSize: "clamp(0.8rem, 3vw, 1.1rem)",
    fontWeight: 600,
    marginLeft: theme.spacing(0.5),
  },
  
  [theme.breakpoints.down(400)]: {
    fontSize: "0.8rem",
    marginLeft: 0,
  },
}));

const RightSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  transition: "gap 0.3s ease",
  
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
  },
  
  [theme.breakpoints.down(400)]: {
    gap: theme.spacing(0.25),
  }
}));

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  display: "none",
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  
  [theme.breakpoints.down("sm")]: {
    display: "flex",
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.75),
  },
}));

const SearchDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: "100%",
    height: "auto",
    maxHeight: "100%",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    
    [theme.breakpoints.down(400)]: {
      padding: theme.spacing(1.5),
    },
  },
}));

const MobileSearchContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(1),
  },
}));

const DesktopSearchContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  margin: theme.spacing(0, 4),
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  flex: 1,
  margin: theme.spacing(0, 4),
  transition: "margin 0.3s ease",
  
  [theme.breakpoints.down("md")]: {
    margin: theme.spacing(0, 2),
  },
  
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const FormatSelect = styled(FormControl)(({ theme }) => ({
  minWidth: 120,
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
}));

const SubscriptionButton = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  textDecoration: "none",
  color: theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.2s ease",
  border: `1px solid ${theme.palette.primary.main}`,
  
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
  },
  
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "none",
  },
  
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    gap: theme.spacing(0.5),
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.5),
    borderWidth: "1px",
  },
}));

const TopBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down(400));
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchFormat, setSearchFormat] = useState("PDF");
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleSearchToggle = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleSearch = (searchQuery, format) => {
    if (!searchQuery.trim()) return;

    // Determine the target path based on format
    const targetPath = format === "PDF" ? "/books" : "/audio-books";

    // Navigate to the appropriate page with search query
    navigate({
      pathname: targetPath,
      search: `?search=${encodeURIComponent(searchQuery)}`,
    });

    // Close mobile search drawer if open
    if (isSearchOpen) {
      handleSearchToggle();
    }
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: "background.paper",
      }}
    >
      <StyledToolbar>
        <LogoContainer to="/">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1.5 },
              maxWidth: { xs: "45%", sm: "none" },
              position: "relative",
              zIndex: 1,
            }}
          >
            <LogoImage
              src={Logo}
              alt="Book Store Logo"
              loading="eager"
              width="48"
              height="48"
              draggable="false"
              sx={{
                WebkitUserDrag: "none",
                WebkitTapHighlightColor: "transparent",
              }}
            />
            <BrandName variant="h1" component="h1">
              {t('topBar.brandName')}
            </BrandName>
          </Box>
        </LogoContainer>

        {/* Desktop Search Section */}
        <Box sx={{ 
          flex: 1, 
          mx: { xs: 1, sm: 2, md: 4 }, 
          display: { xs: "none", sm: "block" } 
        }}>
          <SearchBar onSearch={handleSearch} onFormatChange={setSearchFormat} />
        </Box>

        <RightSection>
          <SearchIconButton
            onClick={handleSearchToggle}
            aria-label="search"
            sx={{ display: { sm: "none" } }}
          >
            <SearchIcon sx={{ fontSize: isExtraSmall ? 20 : 24 }} />
          </SearchIconButton>

          <LanguageSelector />

          <SubscriptionButton to="/subscription" sx={{
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing(0.5, 1),
              minWidth: 'auto',
            },
            [theme.breakpoints.down(400)]: {
              padding: theme.spacing(0.5),
            }
          }}>
            <AutoStoriesIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            <Typography
              variant="button"
              sx={{
                display: { xs: "none", sm: "block" },
                fontSize: {
                  md: '0.875rem',
                  sm: '0.8125rem'
                },
                fontWeight: 500,
              }}
            >
              {t('topBar.subscription')}
            </Typography>
          </SubscriptionButton>

          <Cart />
          <UserMenu />
        </RightSection>
      </StyledToolbar>

      {/* Mobile Search Drawer */}
      <SearchDrawer
        anchor="top"
        open={isSearchOpen && isMobile}
        onClose={handleSearchToggle}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
      >
        <MobileSearchContainer>
          <SearchBar
            autoFocus={true}
            onSearch={handleSearch}
            onFormatChange={setSearchFormat}
            onClose={handleSearchToggle}
            fullWidth
            immediateSearch={true}
          />
        </MobileSearchContainer>
      </SearchDrawer>
    </Box>
  );
};

export default TopBar;
